import React, { useState } from 'react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import cn from 'classnames';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import RetryButton from 'components/Practice/shared/RetryButton';
import { Spinner } from 'ui';
import { useAutosizeVideos } from 'components/shared/hooks/index';
import EmbedForm from '../embed/EmbedForm';
import MediaRecording from './MediaRecording';
import VideoEmbed from '../embed/embed_types/VideoEmbed';
import RecordMenu from '../embed/RecordMenu';

const ExampleRecording = ({
  content,
  browserSupport,
  currentUserId,
  onError,
}) => {
  const [showRecordMenu, setShowRecordMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [embedCode, setEmbedCode] = useState(
    get(content, 'example_recording.embed_code') ||
      get(content, 'exampleRecording.embed_code'),
  );
  const { embedRef, embedContainer } = useAutosizeVideos(embedCode, 0.56);
  const [videoId, setVideoId] = useState(
    get(content, 'example_recording.id') || get(content, 'exampleRecording.id'),
  );
  const questionType =
    get(content, 'question_type') || get(content, 'questionType');

  const isVideo =
    questionType === 'screen_recording' || questionType === 'webcam_recording';
  const [tempEmbedUrl, setTempEmbedUrl] = useState(null);

  // this is to pass into the reused component MediaRecording, it calls this
  const handleUpdate = () => {
    return Promise.resolve(true);
  };

  const submitUrl = async tempUrl => {
    setLoading(true);
    setShowRecordMenu(false);
    request
      .postJSON(routes.QUESTIONS.GOOD_EXAMPLE(content.id), { url: tempUrl })
      .then(response => {
        setVideoId(get(response, 'example_recording.id'));
        setEmbedCode(get(response, 'example_recording.embed_code'));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        if (onError) {
          onError();
        }
      });
  };

  const onChange = params => {
    const tempUrl =
      (content.preview_base || content.previewBase) +
      params.token +
      '?type=lessonly';
    submitUrl(tempUrl);
  };

  const deleteUrl = () => {
    request.delete(
      routes.QUESTIONS.GOOD_EXAMPLE_RECORDING(content.id, videoId),
    );
    setEmbedCode(null);
  };

  return (
    <>
      {showRecordMenu && (
        <div className="pt-16 d-f ai-c jc-c">
          <RecordMenu
            browserSupport={browserSupport}
            onSubmit={tempUrl => submitUrl(tempUrl)}
            toggleRecordMenu={() => setShowRecordMenu(!showRecordMenu)}
            currentUserId={currentUserId}
            isExampleRecording
          />
        </div>
      )}
      {!loading && !embedCode && !showRecordMenu && (
        <div
          className={cn({
            'EditorElement-audioQuestionContainer':
              questionType === 'audio_recording',
          })}
        >
          {questionType === 'audio_recording' ? (
            <MediaRecording
              isExampleRecording
              id={content.id}
              handleUpdate={handleUpdate}
              applyChanges={onChange}
              browserSupport={browserSupport}
              content={content}
            />
          ) : (
            <VideoEmbed
              browserSupport={browserSupport}
              canRecordWithWista={false}
              canRecordInLessonEditor
              currentUserId={currentUserId}
              isExampleRecording
              isExampleScreenRecording={questionType === 'screen_recording'}
              submitRecording={tempUrl => submitUrl(tempUrl)}
              toggleRecordMenu={() => setShowRecordMenu(true)}
            />
          )}

          <div
            className={cn({
              'EditorElement-uploadArea': questionType !== 'audio_recording',
              'EditorElement-audioQuestionContainer--audioForm':
                questionType === 'audio_recording',
            })}
          >
            <EmbedForm
              onSubmit={e => {
                e.preventDefault();
                submitUrl(tempEmbedUrl);
              }}
              onUrlInput={e => setTempEmbedUrl(e.target.value)}
              isVideo={isVideo}
            />
          </div>
        </div>
      )}
      {loading && !embedCode && <Spinner className="p-16 mt-16 mb-16" />}
      {embedCode && (
        <div
          className="EditorElement-exampleRecordingComplete mt-16"
          ref={embedContainer}
        >
          <div className="Video-redo ta-r p-8">
            <RetryButton onClick={() => deleteUrl()} />
          </div>
          <div ref={embedRef} dangerouslySetInnerHTML={{ __html: embedCode }} />
        </div>
      )}
    </>
  );
};

ExampleRecording.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.number,
    question_type: PropTypes.string,
    subject: PropTypes.string,
    raw_choices: PropTypes.string,
    valid: PropTypes.bool,
    zendesk: PropTypes.bool,
    zendesk_support_logo: PropTypes.string,
    example_recording: PropTypes.shape({
      id: PropTypes.number,
      embed_code: PropTypes.string,
    }),
    preview_base: PropTypes.string,
  }),
  currentUserId: PropTypes.number,
  browserSupport: PropTypes.shape({
    audio: PropTypes.bool,
    video: PropTypes.bool,
    screen: PropTypes.bool,
  }),
};

export default ExampleRecording;
